 
import React, { useCallback, useEffect, useState } from 'react';
import { getTaxSettings, getTaxSettingsYears, updateTaxSettings } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { VSAOI_STATUS_OPTIONS, formatDateTime } from '../../lib/workStatus';
import { Percent, Save, CopyPlus } from 'lucide-react';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'iin_rate', label: 'IIN (%)', group: 'Nodokļu likmes' },
  { key: 'nontaxable_minimum', label: 'Neapliekamais minimums (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'dependent_allowance', label: 'Atvieglojums par 1 apgādājamo (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'disability_1', label: 'I invaliditātes grupa (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'disability_2', label: 'II invaliditātes grupa (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'disability_3', label: 'III invaliditātes grupa (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'repressed_allowance', label: 'Politiski represētās personas / pretošanās kustības dalībnieka atvieglojums (€/mēn.)', group: 'Atvieglojumi' },
  { key: 'risk_fee', label: 'Uzņēmējdarbības riska nodeva (€/darbinieku mēnesī)', group: 'Citi' },
  { key: 'minimum_wage', label: 'Minimālā mēnešalga (€)', group: 'Citi' }
];

const TaxSettings = () => {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newYear, setNewYear] = useState('');

  const load = useCallback(async () => {
    try {
      const [data, yearList] = await Promise.all([getTaxSettings(Number(year)), getTaxSettingsYears()]);
      setSettings(data);
      setYears(yearList);
    } catch (error) {
      toast.error('Neizdevās ielādēt nodokļu likmes');
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setSettings((p) => ({ ...p, [key]: value }));
  const setStatusRate = (status, field, value) => setSettings((p) => ({ ...p, vsaoi_statuses: { ...p.vsaoi_statuses, [status]: { ...p.vsaoi_statuses[status], [field]: value } } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, year: Number(settings.year) };
      FIELDS.forEach((f) => { payload[f.key] = Number(payload[f.key]); });
      Object.keys(payload.vsaoi_statuses).forEach((k) => {
        payload.vsaoi_statuses[k] = { ...payload.vsaoi_statuses[k], employee_rate: Number(payload.vsaoi_statuses[k].employee_rate), employer_rate: Number(payload.vsaoi_statuses[k].employer_rate) };
      });
      delete payload.saved; delete payload.updated_at; delete payload.updated_by;
      setSettings(await updateTaxSettings(payload));
      toast.success(`Nodokļu likmes ${payload.year}. gadam saglabātas`);
      setYears(await getTaxSettingsYears());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    } finally {
      setSaving(false);
    }
  };

  const createYear = () => {
    const y = Number(newYear);
    if (!y || y < 2020 || y > 2100) { toast.error('Ievadiet derīgu gadu'); return; }
    setSettings((p) => ({ ...p, year: y, saved: false }));
    setYear(String(y));
    setNewYear('');
    toast.info(`Kopētas ${settings.year}. gada likmes — pielāgojiet un saglabājiet ${y}. gadam`);
  };

  if (!settings) {
    return <div className="flex items-center justify-center h-64" data-testid="admin-tax-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const groups = [...new Set(FIELDS.map((f) => f.group))];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-tax-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B] flex items-center gap-2" data-testid="admin-tax-title"><Percent className="w-6 h-6" /> Algas nodokļu likmes</h1>
          <p className="text-[#2E2E2E]/70">Administrācija → Iestatījumi → Algas → Nodokļu likmes. Katram taksācijas gadam savas likmes; aprēķinā tiek izmantotas attiecīgā gada (vai tuvākā iepriekšējā) likmes.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-tax-save"><Save className="w-4 h-4 mr-2" /> {saving ? 'Saglabā...' : `Saglabāt ${settings.year}. gadam`}</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="admin-tax-year-bar">
        <div className="space-y-1">
          <Label className="text-xs">Taksācijas gads</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid="admin-tax-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`admin-tax-year-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1"><Label className="text-xs">Jauns gads (kopē pašreizējās likmes)</Label><Input type="number" value={newYear} onChange={(e) => setNewYear(e.target.value)} placeholder="2027" className="w-32" data-testid="admin-tax-new-year" /></div>
          <Button variant="outline" onClick={createYear} data-testid="admin-tax-new-year-btn"><CopyPlus className="w-4 h-4 mr-1" /> Izveidot</Button>
        </div>
        {settings.saved ? <Badge className="bg-green-100 text-green-700" data-testid="admin-tax-saved-badge">Saglabāts {formatDateTime(settings.updated_at)} · {settings.updated_by}</Badge> : <Badge className="bg-amber-100 text-amber-700" data-testid="admin-tax-default-badge">Noklusējuma likmes (nav saglabātas)</Badge>}
      </div>

      <Card className="border-0 shadow-sm" data-testid="admin-tax-vsaoi">
        <CardHeader><CardTitle className="text-base">VSAOI likmes pēc darbinieka statusa</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="bg-[#f8faf8] text-left"><tr><th className="px-3 py-2">Statuss</th><th className="px-3 py-2">Darbinieks (%)</th><th className="px-3 py-2">Darba devējs (%)</th><th className="px-3 py-2">Kopā</th></tr></thead>
            <tbody className="divide-y">
              {VSAOI_STATUS_OPTIONS.map((s) => {
                const r = settings.vsaoi_statuses[s.value] || { employee_rate: 0, employer_rate: 0 };
                return (
                  <tr key={s.value} data-testid={`admin-tax-vsaoi-${s.value}`}>
                    <td className="px-3 py-2 font-medium">{s.label}</td>
                    <td className="px-3 py-2"><Input type="number" step="0.01" value={r.employee_rate} onChange={(e) => setStatusRate(s.value, 'employee_rate', e.target.value)} className="w-28" data-testid={`admin-tax-vsaoi-${s.value}-employee`} /></td>
                    <td className="px-3 py-2"><Input type="number" step="0.01" value={r.employer_rate} onChange={(e) => setStatusRate(s.value, 'employer_rate', e.target.value)} className="w-28" data-testid={`admin-tax-vsaoi-${s.value}-employer`} /></td>
                    <td className="px-3 py-2 text-[#2E2E2E]/70">{(Number(r.employee_rate) + Number(r.employer_rate)).toFixed(2)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {groups.map((group) => (
        <Card key={group} className="border-0 shadow-sm" data-testid={`admin-tax-group-${group}`}>
          <CardHeader><CardTitle className="text-base">{group}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.filter((f) => f.group === group).map((f) => (
              <div key={f.key} className="space-y-1">
                <Label className="text-xs">{f.label}</Label>
                <Input type="number" step="0.01" value={settings[f.key]} onChange={(e) => setField(f.key, e.target.value)} data-testid={`admin-tax-${f.key}`} />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-2">
          <Label className="text-xs">Piezīmes (piem. normatīvā akta atsauce)</Label>
          <Textarea rows={2} value={settings.notes || ''} onChange={(e) => setField('notes', e.target.value)} data-testid="admin-tax-notes" />
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxSettings;
